import React, { useState, useRef, useContext, useEffect } from 'react';
import {
  Box, Typography, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronLeftOutlined, ChevronRightOutlined,
  LinkedIn, Language, Twitter,
  EditOutlined, DeleteOutlined, AddOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { v4 as uuid } from 'uuid';
import { PageCtx, CtxMenuCtx, DragCtx  } from '../contexts';
import { SPEAKERS_COLOR } from '../constants';
import { applyDragPreview } from '../utils';
import { SpeakerDrawer } from '../../eventDetailsTab/SpeakersTab';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}
function getHue(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

// ─── Breakpoint-aware prop reader ────────────────────────────────────────────
function getSpkProp(el, bp, key) {
  if (!bp || bp === 'desktop') return el[key];
  return el.responsive?.[bp]?.[key] ?? el[key];
}

// ─── Social links — 25% bigger than before (size default was 17 → now 21) ────
function SocialLinks({ speaker, size = 21, dark = false }) {
  const links = [
    speaker.socialLinks?.linkedin && { icon: <LinkedIn sx={{ fontSize: size }} />, url: speaker.socialLinks.linkedin },
    speaker.socialLinks?.twitter  && { icon: <Twitter  sx={{ fontSize: size }} />, url: speaker.socialLinks.twitter },
    speaker.socialLinks?.website  && { icon: <Language  sx={{ fontSize: size }} />, url: speaker.socialLinks.website },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {links.map((l, i) => (
        <Box key={i}
          onClick={e => { e.stopPropagation(); window.open(l.url, '_blank', 'noopener'); }}
          sx={{
            width: size + 12, height: size + 12, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 0.15s',
            ...(dark
              ? { background: alpha('#000', 0.07), color: '#5a6474', '&:hover': { background: alpha(SPEAKERS_COLOR, 0.12), color: SPEAKERS_COLOR } }
              : { background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.82)', '&:hover': { background: 'rgba(255,255,255,0.28)', color: '#fff' } }
            ),
          }}>
          {l.icon}
        </Box>
      ))}
    </Box>
  );
}

// ─── Card action buttons (edit / delete) ─────────────────────────────────────
function CardActions({ onEdit, onDelete }) {
  return (
    <Box
      className="spk-actions"
      onClick={e => e.stopPropagation()}
      sx={{
        position: 'absolute', top: 8, right: 8, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 0.5,
        opacity: 0, transition: 'opacity 0.15s ease',
        pointerEvents: 'none',
      }}
    >
      <IconButton size="small" onClick={onEdit}
        sx={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
          '&:hover': { background: '#fff', color: SPEAKERS_COLOR }, }}>
        <EditOutlined sx={{ fontSize: 22 }} />
      </IconButton>
      <IconButton size="small" onClick={onDelete}
        sx={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
          '&:hover': { background: '#fff', color: '#d32f2f' }, }}>
        <DeleteOutlined sx={{ fontSize: 22 }} />
      </IconButton>
    </Box>
  );
}

// ─── Photo / initials panel ───────────────────────────────────────────────────
function PhotoPanel({ speaker, cardRadius }) {
  const hue = getHue(speaker.name);
  const initials = getInitials(speaker.name);
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '3.5/4', borderRadius: cardRadius, overflow: 'hidden', flexShrink: 0 }}>
      {speaker.avatar ? (
        <Box component="img" src={speaker.avatar} alt={speaker.name}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(145deg, hsl(${hue},60%,92%) 0%, hsl(${hue},45%,80%) 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Avatar sx={{
            width: 72, height: 72, fontSize: '1.6rem', fontWeight: 700,
            background: `hsl(${hue},58%,46%)`,
            border: '3px solid rgba(255,255,255,0.7)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>{initials}</Avatar>
        </Box>
      )}
    </Box>
  );
}

// ─── Template 1 — full-bleed photo, bio overlay on hover ─────────────────────
function CardT1({ speaker, cardProps, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, cardRadius, textAlign = 'left' } = cardProps;
  const hasBio = speaker.bio || speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative', width: '100%', aspectRatio: '3.5/4', borderRadius: cardRadius,
        overflow: 'hidden', flexShrink: 0, cursor: 'default',
        '&:hover .spk-actions': { opacity: 1, pointerEvents: 'auto' },
      }}>
      <PhotoPanel speaker={speaker} cardRadius={cardRadius} />

      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
        background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 70%, transparent 100%)',
        pointerEvents: 'none' }} />

      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, p: '10px 12px',
        transform: hovered && hasBio ? 'translateY(110%)' : 'translateY(0)',
        transition: 'transform 0.28s ease', textAlign,
      }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && (
          <Typography sx={{ fontSize: titleFontSize, color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            {[speaker.title, speaker.company].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      {hasBio && (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(160deg, rgba(10,20,40,0.93) 0%, rgba(20,40,80,0.89) 100%)',
          backdropFilter: 'blur(2px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          p: '14px 14px 12px',
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          pointerEvents: hovered ? 'auto' : 'none',
        }}>
          <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: '#fff', mb: 0.4, textAlign }}>{speaker.name}</Typography>
          {(speaker.title || speaker.company) && (
            <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', mb: 0.75, textAlign }}>
              {[speaker.title, speaker.company].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {speaker.bio && (
            <Typography sx={{
              fontSize: '11.5px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55, mb: 1, textAlign,
              display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{speaker.bio}</Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start' }}>
            <SocialLinks speaker={speaker} />
          </Box>
        </Box>
      )}

      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </Box>
  );
}

// ─── Template 2 — rectangular photo, info below, scale on hover ──────────────
function CardT2({ speaker, cardProps, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, nameColor, titleColor, cardRadius, textAlign = 'left' } = cardProps;
  const hasSocial = speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{
        width: '100%', display: 'flex', flexDirection: 'column', cursor: 'default',
        position: 'relative',
        transform: hovered ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform 0.22s ease',
        transformOrigin: 'center bottom',
        '&:hover .spk-actions': { opacity: 1, pointerEvents: 'auto' },
      }}>
      <PhotoPanel speaker={speaker} cardRadius={cardRadius} />
      <Box sx={{ pt: 1.25, px: 0.25, textAlign }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: nameColor, lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && (
          <Typography sx={{ fontSize: titleFontSize, color: titleColor, mt: 0.25 }}>
            {[speaker.title, speaker.company].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {speaker.bio && (
          <Typography sx={{
            fontSize: '11.5px', color: alpha(titleColor, 0.85), lineHeight: 1.55, mt: 0.75,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{speaker.bio}</Typography>
        )}
        {hasSocial && (
          <Box sx={{ mt: 0.75, display: 'flex', justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start' }}>
            <SocialLinks speaker={speaker} dark />
          </Box>
        )}
      </Box>
      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </Box>
  );
}

// ─── Template 3 — circle avatar, info below, scale on hover ──────────────────
function CardT3({ speaker, cardProps, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const { nameFontSize, titleFontSize, nameColor, titleColor, textAlign = 'left' } = cardProps;
  const hue = getHue(speaker.name);
  const initials = getInitials(speaker.name);
  const hasSocial = speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;
  const alignItems = textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start';

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      sx={{
        width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
        cursor: 'default', position: 'relative',
        transform: hovered ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform 0.22s ease',
        transformOrigin: 'center bottom',
        '&:hover .spk-actions': { opacity: 1, pointerEvents: 'auto' },
      }}>
      {/* Circle avatar — fills column width, 1:1 via padding trick */}
      <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', flexShrink: 0 }}>
        <Box sx={{
          position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden',
          background: speaker.avatar ? 'none' : `linear-gradient(145deg, hsl(${hue},60%,92%) 0%, hsl(${hue},45%,80%) 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `3px solid ${alpha('#fff', 0.85)}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {speaker.avatar ? (
            <Box component="img" src={speaker.avatar} alt={speaker.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          ) : (
            <Avatar sx={{ width: '55%', height: '55%', fontSize: '1.6rem', fontWeight: 700, background: `hsl(${hue},58%,46%)` }}>
              {initials}
            </Avatar>
          )}
        </Box>
      </Box>

      <Box sx={{ pt: 1.25, textAlign, px: 0.5, width: '100%' }}>
        <Typography sx={{ fontSize: nameFontSize, fontWeight: 700, color: nameColor, lineHeight: 1.2 }}>{speaker.name}</Typography>
        {(speaker.title || speaker.company) && (
          <Typography sx={{ fontSize: titleFontSize, color: titleColor, mt: 0.25 }}>
            {[speaker.title, speaker.company].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {speaker.bio && (
          <Typography sx={{
            fontSize: '11.5px', color: alpha(titleColor, 0.85), lineHeight: 1.55, mt: 0.75,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{speaker.bio}</Typography>
        )}
        {hasSocial && (
          <Box sx={{ mt: 0.75, display: 'flex', justifyContent: alignItems }}>
            <SocialLinks speaker={speaker} dark />
          </Box>
        )}
      </Box>

      {/* CardActions on T3 sit at top-right of the circle area */}
      <Box
        className="spk-actions"
        onClick={e => e.stopPropagation()}
        sx={{
          position: 'absolute', top: 6, right: 6, zIndex: 10,
          display: 'flex', flexDirection: 'column', gap: 0.5,
          opacity: 0, transition: 'opacity 0.15s ease',
          pointerEvents: 'none',
        }}
      >
        <IconButton size="small" onClick={onEdit}
          sx={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
            '&:hover': { background: '#fff', color: SPEAKERS_COLOR } }}>
          <EditOutlined sx={{ fontSize: 22 }} />
        </IconButton>
        <IconButton size="small" onClick={onDelete}
          sx={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', p: 0.9,
            '&:hover': { background: '#fff', color: '#d32f2f' } }}>
          <DeleteOutlined sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Delete confirmation modal ────────────────────────────────────────────────
function DeleteSpeakerModal({ speaker, onConfirm, onCancel }) {
  if (!speaker) return null;
  return (
    <Dialog open onClose={onCancel} onClick={e => e.stopPropagation()}
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 420, width: '100%', p: 0.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: alpha('#d32f2f', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <WarningAmberOutlined sx={{ fontSize: 18, color: '#d32f2f' }} />
        </Box>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>Delete speaker?</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#5a6474', mb: 1 }}>
          <strong>{speaker.name}</strong> will be permanently deleted and removed from any events they are assigned to. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" size="small"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e4e9f0', color: '#5a6474', '&:hover': { borderColor: '#c4cdd8', background: '#f8f9fb' } }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" size="small" color="error"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
          Delete permanently
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Animated carousel track ──────────────────────────────────────────────────
function CarouselTrack({ pages, page, perSlide, gap, CardComponent, cardProps, onEdit, onDelete }) {
  return (
    <Box sx={{ overflow: 'hidden', width: '100%' }}>
      <Box sx={{
        display: 'flex', gap,
        transform: `translateX(calc(-${page * 100}% - ${page} * ${gap}))`,
        transition: 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {pages.map((group, gi) => (
          <Box key={gi} sx={{ display: 'grid', gridTemplateColumns: `repeat(${perSlide}, 1fr)`, gap, minWidth: '100%', flexShrink: 0 }}>
            {Array.from({ length: perSlide }).map((_, ci) => (
              <Box key={ci}>
                {group[ci] ? (
                  <CardComponent
                    speaker={group[ci]}
                    cardProps={cardProps}
                    onEdit={e => { e.stopPropagation(); onEdit(group[ci]); }}
                    onDelete={e => { e.stopPropagation(); onDelete(group[ci]); }}
                  />
                ) : null}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── SpeakersElement ──────────────────────────────────────────────────────────
export function SpeakersElement({ el, selected, onSelect, onDragStart, isDragOver }) {
  const { open: openMenu }                   = useContext(CtxMenuCtx);
  const { speakers = [], onSpeakersUpdate }  = useContext(PageCtx);

  const bp = useContext(DragCtx)?.breakpointRef?.current || 'desktop';

  const displayMode = el.displayMode || 'carousel';
  const template    = el.template    || 1;
  const perSlide    = Math.max(1, Math.min(10, getSpkProp(el, bp, 'perSlide') ?? 4));
  const columns     = Math.max(1, getSpkProp(el, bp, 'columns')  ?? 4);
  const gap         = getSpkProp(el, bp, 'gap')         || '16px';
  const gridJustify = getSpkProp(el, bp, 'gridJustify') || 'center';
  const autoScroll  = el.autoScroll  === true;

  const cardProps = {
    nameColor:    el.nameColor    || '#1a1a2e',
    titleColor:   el.titleColor   || '#5a6474',
    nameFontSize: el.nameFontSize || '15px',
    titleFontSize:el.titleFontSize|| '13px',
    cardRadius:   el.cardRadius   || '12px',
    textAlign:    el.textAlign    || 'left',
  };

  const CardComponent = template === 3 ? CardT3 : template === 2 ? CardT2 : CardT1;

  // Carousel pagination
  const pages = [];
  for (let i = 0; i < speakers.length; i += perSlide) pages.push(speakers.slice(i, i + perSlide));
  const totalPages = Math.max(1, pages.length);

  const [page, setPage] = useState(0);
  const autoRef = useRef(null);

  useEffect(() => { setPage(p => Math.min(p, totalPages - 1)); }, [totalPages]);

  useEffect(() => {
    clearInterval(autoRef.current);
    if (!autoScroll || displayMode !== 'carousel' || totalPages <= 1) return;
    autoRef.current = setInterval(() => setPage(p => (p + 1) % totalPages), 3000);
    return () => clearInterval(autoRef.current);
  }, [autoScroll, displayMode, totalPages]);

  const goTo = (idx, e) => {
    e?.stopPropagation();
    clearInterval(autoRef.current);
    setPage(Math.max(0, Math.min(totalPages - 1, idx)));
    if (autoScroll && totalPages > 1)
      autoRef.current = setInterval(() => setPage(p => (p + 1) % totalPages), 3000);
  };

  // Speaker CRUD state
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [editingSpeaker,setEditingSpeaker]= useState(null);   // null = adding new
  const [deleteTarget,  setDeleteTarget]  = useState(null);   // speaker to confirm delete

  const openAdd  = () => { setEditingSpeaker(null); setDrawerOpen(true); };
  const openEdit = (s) => { setEditingSpeaker(s);   setDrawerOpen(true); };

  const handleSave = (form) => {
    let updated;
    if (editingSpeaker) {
      updated = speakers.map(s => (s.id === editingSpeaker.id || s.name === editingSpeaker.name) ? form : s);
    } else {
      updated = [...speakers, { ...form, id: form.id || uuid() }];
    }
    onSpeakersUpdate?.(updated);
    setDrawerOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    const updated = speakers.filter(s => s.id !== deleteTarget.id && s.name !== deleteTarget.name);
    onSpeakersUpdate?.(updated);
    setDeleteTarget(null);
  };

  return (
    <>
      <Box
        draggable
        onDragStart={e => { applyDragPreview(e, e.currentTarget); onDragStart(e); }}
        onClick={e => { e.stopPropagation(); onSelect(); }}
        onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onSelect(); openMenu(el.id, e.clientX, e.clientY); }}
        sx={{
          position: 'relative', borderRadius: 1, cursor: 'grab', p: '4px',
          outline: isDragOver ? `2px dashed ${alpha(SPEAKERS_COLOR, 0.6)}` : selected ? `2px solid ${SPEAKERS_COLOR}` : '2px solid transparent',
          '&:hover': { outline: selected ? undefined : `2px dashed ${alpha(SPEAKERS_COLOR, 0.35)}` },
        }}
      >
        {selected && (
          <Box sx={{ position: 'absolute', top: -22, left: 0, background: SPEAKERS_COLOR, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px 4px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100 }}>
            Speakers · T{template} · {displayMode}{autoScroll && displayMode === 'carousel' ? ' · auto' : ''}
          </Box>
        )}

        {speakers.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', background: alpha(SPEAKERS_COLOR, 0.04), borderRadius: 2, border: `1px dashed ${alpha(SPEAKERS_COLOR, 0.25)}` }}>
            <Typography sx={{ fontSize: '0.82rem', color: alpha(SPEAKERS_COLOR, 0.6), fontWeight: 500, mb: 1.5 }}>
              No speakers yet
            </Typography>
            <Box onClick={e => { e.stopPropagation(); openAdd(); }}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 2, py: 0.75, borderRadius: 2, border: `1.5px solid ${SPEAKERS_COLOR}`, color: SPEAKERS_COLOR, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, '&:hover': { background: alpha(SPEAKERS_COLOR, 0.06) } }}>
              <AddOutlined sx={{ fontSize: 16 }} /> Add first speaker
            </Box>
          </Box>

        ) : displayMode === 'carousel' ? (
          <Box>
            <CarouselTrack
              pages={pages} page={page} perSlide={perSlide} gap={gap}
              CardComponent={CardComponent} cardProps={cardProps}
              onEdit={openEdit} onDelete={s => setDeleteTarget(s)}
            />

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 2 }}>
                <IconButton size="small" onClick={e => goTo(page - 1, e)} disabled={page === 0}
                  sx={{ border: '1px solid #e4e9f0', background: '#fff', '&:hover': { background: alpha(SPEAKERS_COLOR, 0.07) } }}>
                  <ChevronLeftOutlined sx={{ fontSize: 18 }} />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <Box key={i} onClick={e => goTo(i, e)} sx={{
                      width: i === page ? 22 : 7, height: 7, borderRadius: '4px',
                      cursor: 'pointer', transition: 'all 0.25s ease',
                      background: i === page ? SPEAKERS_COLOR : '#d1d9e0',
                    }} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ea8b3', minWidth: 36, textAlign: 'center' }}>
                  {page + 1} / {totalPages}
                </Typography>
                <IconButton size="small" onClick={e => goTo(page + 1, e)} disabled={page === totalPages - 1}
                  sx={{ border: '1px solid #e4e9f0', background: '#fff', '&:hover': { background: alpha(SPEAKERS_COLOR, 0.07) } }}>
                  <ChevronRightOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>

        ) : (
          // Grid
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap, justifyContent: gridJustify }}>
            {speakers.map(s => (
              <Box key={s.id || s._id || s.name} sx={{ width: `calc((100% - (${columns} - 1) * ${gap}) / ${columns})`, minWidth: 0 }}>
                <CardComponent
                  speaker={s} cardProps={cardProps}
                  onEdit={e => { e.stopPropagation(); openEdit(s); }}
                  onDelete={e => { e.stopPropagation(); setDeleteTarget(s); }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Floating add button — bottom-right of the widget, visible only when selected */}
      {selected && (
        <Box
          onClick={e => { e.stopPropagation(); openAdd(); }}
          sx={{
            position: 'absolute', bottom: 12, right: 12, zIndex: 20,
            height: 40, borderRadius: '999px',
            px: 2, gap: 0.75,
            background: SPEAKERS_COLOR, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(SPEAKERS_COLOR, 0.45)}`,
            cursor: 'pointer', transition: 'all 0.18s',
            '&:hover': { background: '#6d28d9', transform: 'scale(1.05)', boxShadow: `0 6px 20px ${alpha(SPEAKERS_COLOR, 0.55)}` },
          }}
        >
          <AddOutlined sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
            Add speaker
          </Typography>
        </Box>
      )}

      {/* SpeakerDrawer — reuse from SpeakersTab */}
      <SpeakerDrawer
        open={drawerOpen}
        speaker={editingSpeaker}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
      />

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteSpeakerModal
          speaker={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
